import React from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { HistoryRouter } from "redux-first-history/rr6";
import requiresAuthentication from "auth/requiresAuthentication";
import RequireRoles from "auth/RequireRoles";
import ManagerPanel from "manager-panel/ManagerPanel";
import ManagerPanelHome from "home/ManagerPanelHome";
import ManageSipServers from "security-settings/sip-servers/components/ManageSipServers";

import CreatorApplication from "creator-application/CreatorApplication";

import Error from "application/components/Error";
import Login from "application/components/Login";

import ManagePapiServers from "../../security-settings/papi-servers/components/ManagePapiServers";
import UserAdminApplication from "../../user-admin/UserAdminApplication";
import ModuleGroupsApplication from "../../manage-module-groups/ModuleGroupsApplication";
import ManageBadges from "../../manage-badges/ManageBadges";
import ManageSftpAccounts from "../../security-settings/sftp-accounts/components/ManageSftpAccounts";
import ManageRipEndpoints from "../../manage-rip-endpoints/ManageRipEndpoints";
import ManageApplicationAccounts from "../../manage-application-accounts/ManageApplicationAccounts";
import ManageIpOverrides from "../../security-settings/region-ip-override/ManageIpOverrides";
import ManageAuthentication from "../../manage-authentication/ManageAuthentication";
import ManageSubscriptions from "../../manage-subscriptions/ManageSubscriptions";
import ManageOrganizations from "../../manage-organizations/ManageOrganizations";
import ManageCSPs from "../../manage-csp/ManageCSPs";
import ContentIngestionReport from "../../manage-processing/discovery-reporting/ContentIngestionReport";
import ManageContentProviders from "../../manage-content-providers/ManageContentProviders";

import { AlertProvider } from "../../components/alert/AlertProvider";
import { ErrorBoundary } from "react-error-boundary";
import Fallback from "./Fallback";
import { organizeRoles } from "../../auth/authUtils";

const ADMIN = ["ROLE_ADMIN"];
const AuthenticatedManagerPanel = requiresAuthentication(ManagerPanel);

const Application = ({ history }) => {
  return (
    <AlertProvider>
      <HistoryRouter history={history}>
        <ErrorBoundary FallbackComponent={Fallback} onError={console.log}>
          <Routes>
            <Route path="/login/*" element={<Login />} />
            <Route path="/error/*" element={<Error />} />
            <Route
              path="/*"
              element={
                <AuthenticatedManagerPanel>
                  <Routes>
                    <Route index element={<Navigate to="/home" replace />} />

                    <Route path="home/*" element={<ManagerPanelHome />} />

                    <Route
                      path="manage-csp/*"
                      element={
                        <RequireRoles roles={ADMIN}>
                          <ManageCSPs />
                        </RequireRoles>
                      }
                    />

                    <Route
                      path="user-admin/*"
                      element={
                        <RequireRoles roles={ADMIN}>
                          <UserAdminApplication />
                        </RequireRoles>
                      }
                    />

                    <Route
                      path="rip-endpoints/*"
                      element={
                        <RequireRoles roles={ADMIN}>
                          <ManageRipEndpoints />
                        </RequireRoles>
                      }
                    />

                    <Route
                      path="content-providers/*"
                      element={
                        <RequireRoles roles={ADMIN}>
                          <ManageContentProviders />
                        </RequireRoles>
                      }
                    />

                    <Route
                      path="manage-badges/*"
                      element={
                        <RequireRoles roles={ADMIN}>
                          <ManageBadges />
                        </RequireRoles>
                      }
                    />

                    <Route
                      path="manage-organizations/*"
                      element={
                        <RequireRoles roles={ADMIN}>
                          <ManageOrganizations />
                        </RequireRoles>
                      }
                    />

                    <Route
                      path="manage-authentication/*"
                      element={
                        <RequireRoles roles={ADMIN}>
                          <ManageAuthentication />
                        </RequireRoles>
                      }
                    />

                    <Route
                      path="manage-subscriptions/*"
                      element={
                        <RequireRoles roles={ADMIN}>
                          <ManageSubscriptions />
                        </RequireRoles>
                      }
                    />

                    <Route
                      path="region-ip-override/*"
                      element={
                        <RequireRoles roles={ADMIN}>
                          <ManageIpOverrides />
                        </RequireRoles>
                      }
                    />

                    <Route
                      path="sip-servers/*"
                      element={
                        <RequireRoles roles={ADMIN}>
                          <ManageSipServers />
                        </RequireRoles>
                      }
                    />

                    <Route
                      path="sftp-accounts/*"
                      element={
                        <RequireRoles roles={ADMIN}>
                          <ManageSftpAccounts />
                        </RequireRoles>
                      }
                    />

                    <Route
                      path="papi-servers/*"
                      element={
                        <RequireRoles roles={ADMIN}>
                          <ManagePapiServers />
                        </RequireRoles>
                      }
                    />

                    <Route
                      path="manage-application-accounts/*"
                      element={
                        <RequireRoles roles={ADMIN}>
                          <ManageApplicationAccounts />
                        </RequireRoles>
                      }
                    />

                    <Route
                      path="manage-module-groups/*"
                      element={
                        <RequireRoles roles={ADMIN}>
                          <ModuleGroupsApplication />
                        </RequireRoles>
                      }
                    />

                    <Route
                      path="content-ingestion-report/*"
                      element={
                        <RequireRoles roles={ADMIN}>
                          <ContentIngestionReport />
                        </RequireRoles>
                      }
                    />

                    <Route
                      path="creator/*"
                      element={
                        <RequireRoles roles={organizeRoles}>
                          <CreatorApplication />
                        </RequireRoles>
                      }
                    />

                    <Route path="*" element={<Navigate to="/home" replace />} />
                  </Routes>
                </AuthenticatedManagerPanel>
              }
            />
          </Routes>
        </ErrorBoundary>
      </HistoryRouter>
    </AlertProvider>
  );
};

export default Application;
